import { defineStore } from 'pinia';
import { makeProfileReviewGateway } from '~~/app/marketplace/endpoints/profileReviewGateway';
import { MAX_HIGHLIGHTS, PROFILE_REVIEW_DECISION, PROFILE_REVIEW_SOURCE, hasBanner, isDotBand, replayHighlights } from '~~/app/marketplace/support/profileReview';
import type { HighlightToggle, ProfileReview, ProfileReviewDecision, ProfileReviewGateway, ProfileReviewResult, ProfileReviewSource } from '~~/app/marketplace/support/profileReview';
import { useVisibilityProfileStore } from '~/stores/visibilityProfileStore';
import useProfileReviewAccess from '~/composables/useProfileReviewAccess';

export type ProfileReviewEntry = { status: 'loading' } | { status: 'ready'; review: ProfileReview } | { status: 'forbidden' } | { status: 'error' };

export type ProfileReviewOpenRequest = { professionalId: string; source: ProfileReviewSource };

const NO_ACCESS: ProfileReviewResult = { status: 'forbidden' };

/**
 * One profile review per marketplace profile of the tenant. A profile that answers 403 is freemium: it has no
 * entry point anywhere (page, banner, dashboard card, sidebar dot, decline-modal proposal), which every getter
 * here reflects. A user without access to Visibility > My trades gets the same: the store asks the API for
 * nothing and exposes no review, so no entry point has to check the permission on its own.
 */
export const useProfileReviewStore = defineStore('profileReview', () => {
    const profileStore = useVisibilityProfileStore();
    const { access, isChecking } = useProfileReviewAccess();
    const isGranted = computed(() => access.value === 'granted');
    const entries = ref<Record<string, ProfileReviewEntry>>({});
    const loadedAll = ref(false);
    const loadAllFailed = ref(false);
    const openRequest = ref<ProfileReviewOpenRequest | null>(null);
    let gateway: Promise<ProfileReviewGateway> | null = null;

    const getGateway = (): Promise<ProfileReviewGateway> => (gateway ??= makeProfileReviewGateway());

    // Stars clicked and not yet answered, per profile, in click order. The page shows the last API answer with these replayed on top.
    const pendingToggles: Record<string, HighlightToggle[]> = {};
    const confirmed: Record<string, ProfileReview> = {};
    const toggleQueues: Record<string, Promise<unknown>> = {};

    const show = (professionalId: string, review: ProfileReview): void => {
        entries.value[professionalId] = { status: 'ready', review: { ...review, highlights: replayHighlights(review.highlights, pendingToggles[professionalId] ?? []) } };
    };

    // An `error` here is a review that could not be loaded; a write that fails never comes here and keeps the last saved review.
    const apply = (professionalId: string, result: ProfileReviewResult): ProfileReviewResult => {
        if (result.status === 'ok') {
            confirmed[professionalId] = result.review;
            show(professionalId, result.review);
        } else entries.value[professionalId] = { status: result.status };
        return result;
    };

    const reviewOf = (professionalId: string | null | undefined): ProfileReview | null => {
        if (!isGranted.value) return null;
        const entry = professionalId ? entries.value[professionalId] : undefined;
        return entry?.status === 'ready' ? entry.review : null;
    };

    const isForbidden = (professionalId: string | null | undefined): boolean => !!professionalId && entries.value[professionalId]?.status === 'forbidden';

    const isResolved = (professionalId: string | null | undefined): boolean =>
        isGranted.value && !!professionalId && !!entries.value[professionalId] && entries.value[professionalId].status !== 'loading';

    const load = async (professionalId: string, force = false): Promise<ProfileReviewResult> => {
        if (!isGranted.value) return NO_ACCESS;
        const known = reviewOf(professionalId);
        if (known && !force) return { status: 'ok', review: known };

        if (!entries.value[professionalId]) entries.value[professionalId] = { status: 'loading' };
        return apply(professionalId, await (await getGateway()).get(professionalId));
    };

    let loadingAll: Promise<void> | null = null;

    const loadAll = (): Promise<void> => {
        if (!isGranted.value) return Promise.resolve();
        loadingAll ??= (async () => {
            try {
                await Promise.all([profileStore.load(), profileStore.loadIds().then((ids) => Promise.all(ids.map((id) => load(id))))]);
                loadedAll.value = true;
                loadAllFailed.value = false;
            } catch {
                loadingAll = null;
                loadAllFailed.value = true;
            }
        })();
        return loadingAll;
    };

    const post = (professionalId: string, decision: ProfileReviewDecision): Promise<ProfileReviewResult> =>
        getGateway()
            .then((gateway) => gateway.decide(professionalId, decision))
            .catch((): ProfileReviewResult => ({ status: 'error' }));

    /** A decision that cannot be saved leaves the review as last saved and answers `error`: the caller tells the pro. */
    const decide = async (professionalId: string, decision: ProfileReviewDecision): Promise<ProfileReviewResult> => {
        if (!isGranted.value) return NO_ACCESS;
        const result = await post(professionalId, decision);
        return result.status === 'error' ? result : apply(professionalId, result);
    };

    /**
     * Optimistic star: shown at once, sent after the stars clicked before it, so the answers come back in click order.
     * A failed star drops only itself; a 403 keeps the usual effect. A star is not sent when the API already holds three.
     */
    const highlight = (professionalId: string, slug: string, on: boolean): Promise<ProfileReviewResult> => {
        const current = reviewOf(professionalId);
        if (!current) return Promise.resolve(NO_ACCESS);

        const toggle: HighlightToggle = { slug, on };
        const base = confirmed[professionalId] ?? current;
        (pendingToggles[professionalId] ??= []).push(toggle);
        show(professionalId, base);

        const send = async (): Promise<ProfileReviewResult> => {
            const full = on && (confirmed[professionalId] ?? base).highlights.length >= MAX_HIGHLIGHTS;
            const decision = { type: on ? PROFILE_REVIEW_DECISION.HIGHLIGHT : PROFILE_REVIEW_DECISION.UNHIGHLIGHT, slug, source: PROFILE_REVIEW_SOURCE.PROFILE_REVIEW };
            const result: ProfileReviewResult = full ? { status: 'error' } : await post(professionalId, decision);

            pendingToggles[professionalId] = (pendingToggles[professionalId] ?? []).filter((pending) => pending !== toggle);
            if (result.status === 'error') {
                if (entries.value[professionalId]?.status === 'ready') show(professionalId, confirmed[professionalId] ?? base);
                return result;
            }
            return apply(professionalId, result);
        };

        const sent = (toggleQueues[professionalId] ?? Promise.resolve()).then(send);
        toggleQueues[professionalId] = sent.catch(() => undefined);
        return sent;
    };

    const readyReviews = computed<ProfileReview[]>(() => profileStore.profiles.map((profile) => reviewOf(profile.getId())).filter((review): review is ProfileReview => review !== null));

    const bannerProfileIds = computed<string[]>(() => readyReviews.value.filter(hasBanner).map((review) => review.professionalId));

    const needsAttention = computed<boolean>(() => readyReviews.value.some((review) => isDotBand(review.band)));

    const requestOpen = (request: ProfileReviewOpenRequest): void => {
        if (isGranted.value) openRequest.value = request;
    };

    // Skeleton while the access is being checked, then while the reviews load; nothing once access is denied.
    const isLoadingAll = computed<boolean>(() => (isGranted.value ? !loadedAll.value && !loadAllFailed.value : access.value === 'pending' && isChecking.value));

    const takeOpenRequest = (): ProfileReviewOpenRequest | null => {
        const request = openRequest.value;
        openRequest.value = null;
        return isGranted.value ? request : null;
    };

    return {
        access,
        entries,
        loadedAll,
        loadAllFailed,
        isLoadingAll,
        openRequest,
        requestOpen,
        takeOpenRequest,
        reviewOf,
        isForbidden,
        isResolved,
        load,
        loadAll,
        decide,
        highlight,
        bannerProfileIds,
        needsAttention,
    };
});
